/**
 * Everything the AuthCallbackCognito page needs after the Google round-trip.
 * We serialize this into the OAuth `state` param (echoed back verbatim by Cognito)
 * instead of stashing it in web storage.
 */
export type TCognitoAuthState = Partial<{
  externalId: string;
  redirectUrl: string;
  registerResetToken: string;
  email: string;
  productCode: string;
  // Marks a redirect started from the authenticated "link Google" flow (user setting screen).
  linkGoogle: string;
}>;

/** Maps each state field to the query key used inside the serialized `state` blob. */
const STATE_PARAM_KEY: Record<keyof TCognitoAuthState, string> = {
  externalId: "externalId",
  redirectUrl: "redirectUrl",
  productCode: "productCode",
  registerResetToken: "resetToken",
  email: "email",
  linkGoogle: "linkGoogle",
};

const STATE_FIELDS = Object.keys(STATE_PARAM_KEY) as Array<keyof TCognitoAuthState>;

export const encodeCognitoAuthState = (state: TCognitoAuthState): string => {
  const params = new URLSearchParams();

  STATE_FIELDS.forEach((field) => {
    const value = state[field];
    if (value) params.set(STATE_PARAM_KEY[field], value);
  });

  return params.toString();
};

export const decodeCognitoAuthState = (raw: string | null | undefined): TCognitoAuthState => {
  const params = new URLSearchParams(raw ?? "");

  return STATE_FIELDS.reduce<TCognitoAuthState>((state, field) => {
    const value = params.get(STATE_PARAM_KEY[field]);
    if (value) state[field] = value;
    return state;
  }, {});
};
